import { ReactNode } from 'react';
import { Tag, Tooltip } from 'antd';
import { colors } from 'theme/tokens';
import {
  ACCOUNT_STATUS_LABEL,
  ITEM_STATUS_COLOR,
  ITEM_STATUS_LABEL,
  LEDGER_KIND_COLOR,
  LEDGER_KIND_LABEL,
  MISSION_STATUS_COLOR,
  MISSION_STATUS_LABEL,
  NO_GROUP_LABEL,
  ORDER_STATUS_COLOR,
  ORDER_STATUS_LABEL,
  PENALTY_STATUS_COLOR,
  PENALTY_STATUS_LABEL,
  PROGRESS_STATUS_COLOR,
  PROGRESS_STATUS_LABEL,
  STAR_GROUP_COLOR,
  STAR_GROUP_LABEL,
  labelOf,
} from '../labels';
import {
  ItemStatus,
  LedgerKind,
  MissionStatus,
  OrderStatus,
  PenaltyStatus,
  ProgressStatus,
  StarAccountStatus,
  StarGroup,
  StarUserRef,
} from '../types';
import { personName } from '../utils';

const tagStyle = { borderRadius: 6, padding: '2px 8px', marginInlineEnd: 0 };

export const StarGroupTag = ({ value }: { value?: StarGroup | null }) =>
  value ? (
    <Tag color={STAR_GROUP_COLOR[value]} style={tagStyle}>
      {labelOf(STAR_GROUP_LABEL, value)}
    </Tag>
  ) : (
    <Tag style={tagStyle}>{NO_GROUP_LABEL}</Tag>
  );

export const AccountStatusTag = ({ value }: { value?: StarAccountStatus }) => (
  <Tag color={value === 'FROZEN' ? 'red' : 'green'} style={tagStyle}>
    {labelOf(ACCOUNT_STATUS_LABEL, value)}
  </Tag>
);

export const LedgerKindTag = ({ value }: { value?: LedgerKind }) => (
  <Tag color={value ? LEDGER_KIND_COLOR[value] : 'default'} style={tagStyle}>
    {labelOf(LEDGER_KIND_LABEL, value)}
  </Tag>
);

export const MissionStatusTag = ({ value }: { value?: MissionStatus }) => (
  <Tag color={value ? MISSION_STATUS_COLOR[value] : 'default'} style={tagStyle}>
    {labelOf(MISSION_STATUS_LABEL, value)}
  </Tag>
);

export const ProgressStatusTag = ({ value }: { value?: ProgressStatus }) => (
  <Tag color={value ? PROGRESS_STATUS_COLOR[value] : 'default'} style={tagStyle}>
    {labelOf(PROGRESS_STATUS_LABEL, value)}
  </Tag>
);

export const ItemStatusTag = ({ value }: { value?: ItemStatus }) => (
  <Tag color={value ? ITEM_STATUS_COLOR[value] : 'default'} style={tagStyle}>
    {labelOf(ITEM_STATUS_LABEL, value)}
  </Tag>
);

export const OrderStatusTag = ({ value }: { value?: OrderStatus }) => (
  <Tag color={value ? ORDER_STATUS_COLOR[value] : 'default'} style={tagStyle}>
    {labelOf(ORDER_STATUS_LABEL, value)}
  </Tag>
);

export const PenaltyStatusTag = ({ value }: { value?: PenaltyStatus }) => (
  <Tag color={value ? PENALTY_STATUS_COLOR[value] : 'default'} style={tagStyle}>
    {labelOf(PENALTY_STATUS_LABEL, value)}
  </Tag>
);

// Tên + username/email phụ, dùng cho cột "Người dùng" ở mọi bảng.
// Dòng phụ (email dài) cắt "…" theo độ rộng ô, rê chuột xem đủ — không tràn sang cột bên.
export const UserCell = ({ user, extra }: { user?: StarUserRef | null; extra?: string | null }) => {
  if (!user) return <span>—</span>;
  const sub = [user.username, user.email].filter(Boolean).join(' · ');
  const line = extra || sub;
  const tip = [extra, sub].filter(Boolean).join(' — ');
  return (
    <Tooltip title={tip || undefined}>
      <span style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.3, minWidth: 0 }}>
        <span style={{ fontWeight: 600, overflowWrap: 'anywhere' }}>{personName(user)}</span>
        {line && (
          <span
            style={{
              color: colors.textSecondary, // #8c8c8c cũ chỉ 3.4:1
              fontSize: 12,
              maxWidth: 280,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {line}
          </span>
        )}
      </span>
    </Tooltip>
  );
};

// Giữ ngày giờ / số trên một dòng ("28/09/2026 11:27" không bị tách đôi).
// Cột chứa nó phải đủ rộng: ngày giờ DD/MM/YYYY HH:mm cần width ≥ 160.
export const NoWrap = ({ children }: { children: ReactNode }) => (
  <span style={{ whiteSpace: 'nowrap' }}>{children}</span>
);

// Chữ một dòng, dài quá thì cắt "…" (rê chuột xem đủ). `width` chỉ là độ rộng TỐI ĐA:
// ô hẹp hơn thì chữ tự co theo ô (block + minWidth 0), không tràn sang cột bên cạnh.
// Đặt trong hàng flex thì nó là phần tử co được; phần tử bên cạnh nên flexShrink: 0.
export const Ellipsis = ({ value, width = 220 }: { value?: string | null; width?: number }) => (
  <Tooltip title={value || undefined}>
    <span
      style={{
        display: 'block',
        maxWidth: width,
        minWidth: 0,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
      }}
    >
      {value || '—'}
    </span>
  </Tooltip>
);
